import type { ExperienceConfiguration } from "@ninetailed/experience.js-react";
import { type ExperienceLike, ExperienceMapper } from "@ninetailed/experience.js-utils";
import { vercelStegaClean } from "@vercel/stega";
import type { Entry } from "contentful";
import { getEntriesOfContentType, getEntryOfContentType, isEntryOfContentType } from "@/lib/contentful/guards";
import type { TypeNt_audienceSkeleton, TypeNt_experienceSkeleton } from "@/types/contentful/generated";

type AudienceEntry = Entry<TypeNt_audienceSkeleton, undefined, string>;
type ExperienceEntry = Entry<TypeNt_experienceSkeleton, undefined, string>;

export interface VariantData<TFields> {
  id: string;
  fields: TFields;
}

type AudienceLike<TFields> = ExperienceLike<VariantData<TFields>>["audience"];
type ConfigLike<TFields> = ExperienceLike<VariantData<TFields>>["config"];

// Contentful types nt_config as opaque JSON. Its authored shape is the Ninetailed
// experience config, but that can't be verified structurally, so we narrow to a
// plain object here; ExperienceMapper re-validates the contents at runtime.
function toConfig<TFields>(value: unknown): ConfigLike<TFields> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  return value as ConfigLike<TFields>;
}

function toAudience<TFields>(audience: unknown): AudienceLike<TFields> {
  const entry = getEntryOfContentType<AudienceEntry>(audience, "nt_audience");
  if (!entry) return undefined;
  return {
    id: vercelStegaClean(entry.fields.nt_audience_id ?? entry.sys.id),
    name: vercelStegaClean(entry.fields.nt_name),
  };
}

function toVariants<TFields>(variants: unknown, contentTypeId: string): VariantData<TFields>[] {
  if (!Array.isArray(variants)) return [];
  const result: VariantData<TFields>[] = [];
  for (const value of variants) {
    if (!isEntryOfContentType(value, contentTypeId)) continue;
    result.push({ id: value.sys.id, fields: value.fields as TFields });
  }
  return result;
}

function toExperienceLike<TFields>(
  entry: ExperienceEntry,
  variantContentType: string,
): ExperienceLike<VariantData<TFields>> {
  const { fields, sys } = entry;
  // In draft mode the preview client stega-encodes every string field (including
  // nt_experience_id / nt_type). The preview plugin and the SDK's variant selection
  // compare these ids strictly against clean ids (widget config, Ninetailed API
  // responses), so polluted metadata silently breaks variant forcing and draft
  // selection. Clean the metadata only — variant `fields` keep their encoding,
  // which powers the Contentful live-preview inspector.
  return {
    id: vercelStegaClean(fields.nt_experience_id ?? sys.id),
    name: vercelStegaClean(fields.nt_name),
    type: vercelStegaClean(fields.nt_type),
    config: toConfig(fields.nt_config),
    audience: toAudience(fields.nt_audience),
    variants: toVariants(fields.nt_variants, variantContentType),
  };
}

/**
 * Transforms `nt_experiences` references on any personalizable Contentful entry
 * into the experience configurations the Ninetailed `<Experience>` component expects.
 * Variants are filtered to entries of `variantContentType` — typically the same
 * content type as the baseline. Unresolved links and other entry types are dropped.
 */
export function mapEntryExperiences<TFields>(
  experiences: unknown[] | undefined,
  variantContentType: string,
): ExperienceConfiguration<VariantData<TFields>>[] {
  if (!experiences) return [];
  return getEntriesOfContentType<ExperienceEntry>(experiences, "nt_experience")
    .map(entry => toExperienceLike<TFields>(entry, variantContentType))
    .filter(experience => ExperienceMapper.isExperienceEntry(experience))
    .map(experience => ExperienceMapper.mapExperience(experience));
}
